// @ts-check

/** @file Stable identity and schema constants for project-bound replicas. */

import localDatabaseModel from "./local-database-model.js"

export const PROJECT_REPLICA_DATABASE_IDENTIFIER = "projectReplica"
export const PROJECT_REPLICA_SCHEMA_GENERATION = "awesome-tasks-project-replica-20260924190000"

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu
const ENVIRONMENT_PATTERN = /^[a-z0-9_-]+$/u

/** @typedef {Readonly<{projectId: string, userId: string}>} ProjectReplicaIdentity */

/**
 * Validates the complete security/storage identity for one replica.
 * @param {{projectId?: unknown, userId?: unknown}} args Candidate identity.
 * @returns {ProjectReplicaIdentity} Canonical immutable identity.
 * @throws {Error} When either UUID is absent or malformed.
 */
export function projectReplicaIdentity({projectId, userId}) {
  return Object.freeze({
    projectId: canonicalUuid(projectId, "project"),
    userId: canonicalUuid(userId, "user")
  })
}

/**
 * Builds a filesystem/storage-safe injective name from two canonical UUIDs.
 * @param {{environment: unknown, projectId: unknown, userId: unknown}} args Name inputs.
 * @returns {string} Deterministic physical SQLite name.
 * @throws {Error} When environment or either identity component is invalid.
 */
export function projectReplicaDatabaseName({environment, projectId, userId}) {
  if (typeof environment !== "string" || !ENVIRONMENT_PATTERN.test(environment)) {
    throw new Error("Project replica requires a valid environment name")
  }

  const identity = projectReplicaIdentity({projectId, userId})
  const userKey = identity.userId.replaceAll("-", "")
  const projectKey = identity.projectId.replaceAll("-", "")

  return `awesome-tasks-${environment}-replica-user-${userKey}-project-${projectKey}`
}

/** Project-domain name for the shared native-safe local model constructor. */
export const projectReplicaModel = localDatabaseModel

/**
 * Canonicalizes one required UUID component.
 * @param {unknown} value Candidate UUID.
 * @param {"project" | "user"} label Identity label.
 * @returns {string} Lowercase value safe for identity keys.
 * @throws {Error} When the value is not a canonical UUID.
 */
function canonicalUuid(value, label) {
  if (typeof value !== "string" || !UUID_PATTERN.test(value)) {
    throw new Error(`Project replica requires a valid ${label} UUID`)
  }

  return value.toLowerCase()
}
